import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * Option names that exist on a variant published to the Online Store.
 * @typedef {object} PublishedCatalog
 * @property {boolean} complete
 * @property {{ name: string, values: Set<string> }[]} options
 */

/** @type {Map<string, PublishedCatalog | null>} */
const publishedCatalogByUrl = new Map();

/** @type {Map<string, Promise<PublishedCatalog | null>>} */
const publishedCatalogRequests = new Map();

/**
 * @param {string | undefined} productUrl
 * @returns {string}
 */
function catalogKey(productUrl) {
  if (!productUrl) return '';

  return productUrl.split('?')[0].replace(/\.js$/, '');
}

/**
 * Storefront catalog. The theme editor can still print unpublished colors.
 * product.js does not. A list of 250 variants is truncated, so it must not hide a real color.
 * @param {string | undefined} productUrl
 * @returns {Promise<PublishedCatalog | null>}
 */
function loadPublishedCatalog(productUrl) {
  const url = catalogKey(productUrl);

  if (!url) return Promise.resolve(null);

  if (publishedCatalogByUrl.has(url)) {
    return Promise.resolve(publishedCatalogByUrl.get(url) ?? null);
  }

  const pending = publishedCatalogRequests.get(url);

  if (pending) return pending;

  const request = fetch(`${url}.js`, { credentials: 'same-origin' })
    .then((response) => (response.ok ? response.json() : null))
    .then((product) => {
      if (!product || !Array.isArray(product.variants)) {
        publishedCatalogByUrl.set(url, null);
        return null;
      }

      if (product.variants.length >= 250) {
        const incomplete = { complete: false, options: [] };
        publishedCatalogByUrl.set(url, incomplete);
        return incomplete;
      }

      const options = Array.isArray(product.options)
        ? product.options.map((option) => ({
            name: String(option?.name ?? ''),
            values: new Set(
              Array.isArray(option?.values) ? option.values.filter((value) => value != null).map(String) : []
            ),
          }))
        : [];

      /** @type {PublishedCatalog} */
      const catalog = { complete: true, options };
      publishedCatalogByUrl.set(url, catalog);
      return catalog;
    })
    .catch(() => {
      publishedCatalogByUrl.set(url, null);
      return null;
    });

  publishedCatalogRequests.set(url, request);
  return request;
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {number} */
  #availabilityResolveAttempts = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#rebuildFieldsetState();
    this.addEventListener('change', this.variantChanged.bind(this));

    if (this.tagName !== 'VARIANT-PICKER') return;

    const productUrl = this.dataset.productUrl;

    void loadPublishedCatalog(productUrl).then((catalog) => {
      if (!this.isConnected || !catalog?.complete) return;
      if (catalogKey(this.dataset.productUrl) !== catalogKey(productUrl)) return;

      this.#applyPublishedCatalog(this, catalog);
      this.#rebuildFieldsetState();
    });
  }

  /**
   * Whether theme setting "Ocultar variantes sem estoque" is active on this picker.
   * @returns {boolean}
   */
  get hideUnavailableVariants() {
    return this.dataset.hideUnavailableVariants === 'true';
  }

  /**
   * Whether the block setting blocks selecting unavailable options.
   * @returns {boolean}
   */
  get disableUnavailableSelection() {
    return this.dataset.disableUnavailableSelection === 'true';
  }

  /**
   * Auto-resolve when hiding unavailable options or when selection of sold-out is blocked.
   * @returns {boolean}
   */
  get shouldResolveUnavailable() {
    return this.hideUnavailableVariants || this.disableUnavailableSelection;
  }

  /**
   * Fieldsets from the live DOM (refs can lag one tick after morph).
   * @returns {HTMLFieldSetElement[]}
   */
  #getFieldsets() {
    return Array.from(this.querySelectorAll('fieldset'));
  }

  /**
   * Rebuilds radio caches after morph / connect so fieldset indexes stay aligned.
   */
  #rebuildFieldsetState() {
    this.#radios = [];
    this.#checkedIndices = [];

    for (const fieldset of this.#getFieldsets()) {
      const radios = Array.from(fieldset.querySelectorAll('input'));
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex(
        (radio) => radio.checked || radio.dataset.currentChecked === 'true'
      );
      this.#checkedIndices.push(initialCheckedIndex !== -1 ? [initialCheckedIndex] : []);
    }
  }

  /**
   * True when a fieldset/select has no checked option or the checked one is unavailable.
   * @returns {boolean}
   */
  #hasIncompleteOrUnavailableSelection() {
    for (const fieldset of this.#getFieldsets()) {
      const checked = fieldset.querySelector('input[type="radio"]:checked');

      if (!(checked instanceof HTMLInputElement)) return true;
      if (checked.dataset.optionAvailable === 'false') return true;
    }

    for (const select of this.querySelectorAll('select')) {
      if (!(select instanceof HTMLSelectElement) || select.options.length === 0) continue;

      const selected = select.querySelector('option[selected]');

      if (!(selected instanceof HTMLOptionElement)) return true;
      if (selected.dataset.optionAvailable === 'false') return true;
    }

    return false;
  }

  /**
   * Selects the first available option in each incomplete fieldset/select and re-fetches.
   * Only runs when `hide_unavailable_variants` is enabled.
   *
   * @returns {boolean} Whether a re-fetch was triggered
   */
  selectFirstAvailableVariant() {
    if (!this.shouldResolveUnavailable) return false;
    if (this.#availabilityResolveAttempts >= 2) return false;
    if (!this.#hasIncompleteOrUnavailableSelection()) return false;

    this.#rebuildFieldsetState();

    /** @type {HTMLElement | null} */
    let anchorOption = null;
    let didChange = false;

    for (const fieldset of this.#getFieldsets()) {
      const checked = fieldset.querySelector('input[type="radio"]:checked');

      if (checked instanceof HTMLInputElement && checked.dataset.optionAvailable !== 'false') {
        continue;
      }

      const firstAvailable =
        fieldset.querySelector('input[type="radio"][data-option-available="true"]') ||
        fieldset.querySelector('input[type="radio"]:not([data-option-available="false"])');

      if (!(firstAvailable instanceof HTMLInputElement)) continue;
      if (checked === firstAvailable) continue;

      this.updateSelectedOption(firstAvailable);
      anchorOption = firstAvailable;
      didChange = true;
    }

    for (const select of this.querySelectorAll('select')) {
      if (!(select instanceof HTMLSelectElement) || select.options.length === 0) continue;

      const selected = select.querySelector('option[selected]');
      const selectedOk =
        selected instanceof HTMLOptionElement && selected.dataset.optionAvailable !== 'false';

      if (selectedOk) continue;

      const firstAvailable = Array.from(select.options).find(
        (option) => option.value !== '' && option.dataset.optionAvailable !== 'false'
      );

      if (!firstAvailable) continue;
      if (selected === firstAvailable) continue;

      select.value = firstAvailable.value;
      this.updateSelectedOption(select);
      anchorOption = firstAvailable;
      didChange = true;
    }

    if (!didChange || !anchorOption) return false;

    this.#availabilityResolveAttempts += 1;

    this.dispatchEvent(
      new VariantSelectedEvent({ id: anchorOption.dataset.optionValueId ?? '' })
    );
    this.#syncUrlFromSelectedOption(anchorOption);

    if (document.activeElement instanceof HTMLElement && this.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    this.fetchUpdatedSection(this.buildRequestUrl(anchorOption));

    return true;
  }

  /**
   * Updates the browser URL for the selected option (PDP only).
   * @param {HTMLElement} selectedOption
   */
  #syncUrlFromSelectedOption(selectedOption) {
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog');

    if (!isOnProductPage) return;

    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId || null;
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const loadsNewProduct = !!newUrl && newUrl !== currentUrl;

    if (variantId) {
      url.searchParams.set('variant', variantId);
    } else {
      url.searchParams.delete('variant');
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    if (
      this.disableUnavailableSelection &&
      selectedOption.dataset.optionAvailable === 'false'
    ) {
      return;
    }

    this.#availabilityResolveAttempts = 0;
    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = this.#getFieldsets();
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const catalogReady = loadPublishedCatalog(this.#pendingRequestUrl || this.dataset.productUrl);

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then(async (responseText) => {
        await catalogReady;
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // After hide-unavailable morph, auto-pick first available options if selection broke.
          if (this.selectFirstAvailableVariant()) {
            return;
          }

          this.#availabilityResolveAttempts = 0;

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;
      const sameProduct = !newProductId || newProductId === this.dataset.productId;
      const catalog = publishedCatalogByUrl.get(
        catalogKey(newVariantPickerSource.dataset.productUrl || this.dataset.productUrl)
      );

      if (sameProduct && catalog?.complete) {
        this.#applyPublishedCatalog(newVariantPickerSource, catalog);
      } else if (sameProduct) {
        this.#dropOptionsMissingFromStorefront(newVariantPickerSource);
      }

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;

      if (newVariantPickerSource.dataset.hideUnavailableVariants === 'true') {
        this.dataset.hideUnavailableVariants = 'true';
      } else {
        delete this.dataset.hideUnavailableVariants;
      }

      if (newVariantPickerSource.dataset.disableUnavailableSelection === 'true') {
        this.dataset.disableUnavailableSelection = 'true';
      } else {
        delete this.dataset.disableUnavailableSelection;
      }
    }

    morph(this, newVariantPickerSource);
    this.#rebuildFieldsetState();

    return newProduct;
  }

  /**
   * Drops option values that are not in the published catalog.
   * A linked product (combined listing) keeps its own colors.
   * @param {ParentNode} root
   * @param {PublishedCatalog | null | undefined} catalog
   */
  #applyPublishedCatalog(root, catalog) {
    if (!catalog?.complete || catalog.options.length === 0) return;

    const options = [...catalog.options].sort((a, b) => b.name.length - a.name.length);

    for (const input of root.querySelectorAll('[data-option-value-id]')) {
      if (!(input instanceof HTMLElement)) continue;
      if (input.dataset.connectedProductUrl) continue;

      const name =
        input instanceof HTMLOptionElement
          ? input.closest('select')?.name || ''
          : input instanceof HTMLInputElement
            ? input.name
            : '';

      if (!name) continue;

      const option = options.find((item) => name === item.name || name.startsWith(`${item.name}-`));

      if (!option) continue;

      const value = input instanceof HTMLInputElement || input instanceof HTMLOptionElement ? input.value : '';

      if (!value || option.values.has(value)) continue;

      const row = input.closest('li') || input.closest('label') || input;
      row.remove();
    }
  }

  /**
   * The section response can include colors the storefront did not publish.
   * Those values are absent on first paint and must not appear after a selection.
   * Used only before the published catalog has loaded.
   * @param {HTMLElement} source
   */
  #dropOptionsMissingFromStorefront(source) {
    /** @type {Set<string>} */
    const published = new Set();

    for (const input of this.querySelectorAll('[data-option-value-id]')) {
      if (input instanceof HTMLElement && input.dataset.optionValueId) {
        published.add(input.dataset.optionValueId);
      }
    }

    if (published.size === 0) return;

    for (const input of source.querySelectorAll('[data-option-value-id]')) {
      if (!(input instanceof HTMLElement)) continue;

      const id = input.dataset.optionValueId;
      if (!id || published.has(id)) continue;

      const row = input.closest('li') || input.closest('label') || input;
      row.remove();
    }
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
